import {
  Box,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  useDisclosure,
  Center,
  Separator,
  Portal,
} from "@chakra-ui/react"
import DescriptionDefinition from "@hydrologiq/shared/chakra/custom/DescriptionDefinition"
import DescriptionList from "@hydrologiq/shared/chakra/custom/DescriptionList"
import DescriptionTerm from "@hydrologiq/shared/chakra/custom/DescriptionTerm"
import { DataPoint } from "../../monitoring/builders/1.0.0/graphs/TimeSeries"
import { roundToDP } from "@hydrologiq/common/helpers/math"
import { toKey } from "@hydrologiq/common/test/toKey"
import { milliseconds } from "timezonecomplete"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import { RefObject, useRef, useEffect } from "react"
import React from "react"

export const DATA_POINT_GROUPS = ["Average", "Peak"]

function HoverPopover({
  x,
  y,
  disclosure,
  dataPoints,
  unit,
  startOffsetMS,
  container,
  groupLabels,
}: {
  x: number
  y: number
  disclosure: ReturnType<typeof useDisclosure>
  dataPoints: DataPoint[]
  unit?: string
  startOffsetMS?: number
  container?: RefObject<HTMLElement | null>
  groupLabels?: string[]
}) {
  const dontShowGroups = [2]
  const filteredDataPoints = dataPoints.filter((value) => {
    const groupValue = parseInt(value.group)
    return !dontShowGroups.includes(groupValue) && groupValue >= 0
  })
  const dateFormat = "dd MMM  HH:mm"
  const contentRef = useRef<HTMLDivElement>(null)

  // Update popover position when coordinates change
  useEffect(() => {
    if (contentRef.current && disclosure.open) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (!contentRef.current) return

        // Calculate popover position: centered above trigger point
        // Popover width is maxWidth={200}, so center it on x
        const popoverWidth = 200 // matches maxWidth prop
        const offsetX = popoverWidth / 2
        const offsetY = 10 // space above trigger point

        // Add transition for smooth movement
        contentRef.current.style.transition = "left 0.15s ease-out, top 0.15s ease-out"
        contentRef.current.style.position = "fixed"
        const leftPos = x - offsetX
        const topPos = y - offsetY
        contentRef.current.style.left = `${leftPos}px`
        contentRef.current.style.top = `${topPos}px`
        contentRef.current.style.transform = "translateY(-100%)" // Position above

        // Position arrow at bottom, aligned with trigger point (x, y)
        // Arrow should align horizontally with trigger point, not popover center
        // Calculate arrow position relative to popover's left edge
        const arrowOffsetFromLeft = x - leftPos // Distance from popover left to trigger x

        // Find arrow element with multiple strategies
        let arrowElement: HTMLElement | null = contentRef.current.querySelector('[data-part="arrow"]') as HTMLElement

        if (arrowElement) {
          arrowElement.style.transition = "left 0.15s ease-out"
          arrowElement.style.position = "absolute"
          arrowElement.style.left = `${arrowOffsetFromLeft}px` // Align with trigger point
          arrowElement.style.bottom = "-6px" // Position to overlap with popover body slightly

          // invert the arrow
          arrowElement.style.transform = "scaleY(-1) translateX(-42.5%)"
        }
      })
    }
  }, [x, y, disclosure.open])
  return (
    <>
      <Portal container={container}>
        {filteredDataPoints && filteredDataPoints.length > 0 && (
          <Popover.Root
            defaultOpen={true}
            open={disclosure.open}
            onOpenChange={(e) => disclosure.setOpen(e.open)}
            closeOnInteractOutside={false}
            closeOnEscape={true}
            portalled
          >
            <PopoverTrigger asChild>
              <Box
                zIndex={0}
                position={"fixed"}
                width={"6px"}
                height={"0px"}
                role="button"
                style={{
                  transition: "none",
                  left: `${x}px`,
                  top: `${y}px`,
                  position: "fixed"
                }}
              />
            </PopoverTrigger>
            <PopoverContent
              ref={contentRef}
              _focus={{ border: "none" }}
              color="white"
              bg="rgba(57, 174, 210, 0.9)"
              maxWidth={200}
              fontSize={13}
              pointerEvents={"auto"}
              css={{
                "--popover-bg": "rgba(57, 174, 210, 0.75)",
              }}
              style={{
                position: "fixed",
                transition: "left 0.15s ease-out, top 0.15s ease-out",
                zIndex: 1, // Ensure popover body is above arrow
                border: "none",
                outline: "none"
              }}
            >
              <PopoverArrow />
              <PopoverHeader textAlign={"center"} width={"100%"} display={"flex"} pb={0} border="0">
                <DescriptionList>
                  <HStack pt={0} width={"100%"} justifyContent={"space-around"}>
                    <Box textAlign={"center"} pb={1}>
                      <DescriptionTerm fontWeight={"bold"}>Start</DescriptionTerm>
                      <DescriptionDefinition fontSize={15}>
                        {startOffsetMS &&
                          toUserTZ(filteredDataPoints[0].time.sub(milliseconds(startOffsetMS))).format(dateFormat)}
                      </DescriptionDefinition>
                    </Box>
                    <Box textAlign={"center"} pb={1}>
                      <DescriptionTerm fontWeight={"bold"}>End</DescriptionTerm>
                      <DescriptionDefinition fontSize={15}>
                        {toUserTZ(filteredDataPoints[0].time).format(dateFormat)}
                      </DescriptionDefinition>
                    </Box>
                  </HStack>
                </DescriptionList>
              </PopoverHeader>

              <Center height="10px">
                <Separator orientation="horizontal" />
              </Center>
              <PopoverBody pt={0} width={"100%"} fontSize={14}>
                <DescriptionList>
                  <HStack pt={0} width={"100%"} justifyContent={"space-around"}>
                    {filteredDataPoints.map((point) => {
                      const groupIndex = parseInt(point.group)
                      return DATA_POINT_GROUPS.length > groupIndex ? (
                        <Box
                          key={toKey(`${point.group}-${point.time.unixUtcMillis()}`)}
                          textAlign={"center"}
                          pb={1}
                          pt={0}
                          rounded={"xl"}
                          width={"100%"}
                        >
                          <DescriptionTerm fontWeight={"bold"}>
                            {groupLabels ? groupLabels[groupIndex] : DATA_POINT_GROUPS[groupIndex]}
                          </DescriptionTerm>
                          <DescriptionDefinition fontSize={15}>
                            {roundToDP(point.value, 2)} {unit}
                          </DescriptionDefinition>
                        </Box>
                      ) : (
                        <></>
                      )
                    })}
                  </HStack>
                </DescriptionList>
              </PopoverBody>
            </PopoverContent>
          </Popover.Root>
        )}
      </Portal>
    </>
  )
}

export default React.memo(HoverPopover, (prevProps, nextProps) => {
  // Re-render if disclosure state changed
  if (prevProps.disclosure.open !== nextProps.disclosure.open) {
    return false
  }
  // Re-render if dataPoints array length changed
  if (prevProps.dataPoints.length !== nextProps.dataPoints.length) {
    return false
  }
  // Re-render if dataPoints content changed (time, value, or group) - this includes new point selection
  if (prevProps.dataPoints.some((point, index) => {
    const nextPoint = nextProps.dataPoints[index]
    return !nextPoint ||
      point.time.unixUtcMillis() !== nextPoint.time.unixUtcMillis() ||
      point.value !== nextPoint.value ||
      point.group !== nextPoint.group
  })) {
    return false
  }
  // Always re-render if coordinates changed to ensure popover position updates
  // The threshold in TSWrapper prevents micro-movements from causing coordinate updates
  if (prevProps.x !== nextProps.x || prevProps.y !== nextProps.y) {
    return false
  }
  // Skip re-render only if everything is unchanged
  return true
})
